import React, { useEffect, useMemo, useState } from 'react';

import { Grid, Divider, Typography, Box } from '@material-ui/core';
import { isEqual, merge } from 'lodash';

import { CardComponent, PageHeader, SettingsForm, ColoredBox } from 'components';
import { TextValidatorComponent } from 'components/FormComponents';
import { ConfirmationDialog } from 'components/Dialogs';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { UPLOAD_STATUSES } from 'contexts/UploadStatusContext/constants';
import { VALIDATORS_MAP } from 'components/FormComponents/const';
import { StatusComponent } from 'pages/planograms/StatusComponent/StatusComponent';
import { useSubmittingState } from 'hooks';
import { analyticsProviderModel } from 'models/AnalyticsProviderModel';
import { customDomainsModel } from 'models/CustomDomainsModel';
import { ROUTES } from 'routes/constants';
import { MatomoAnalyticsForm } from 'types/other';
import { CustomDomain } from 'types/domains';
import { getTWithPathKey } from 'utils';

import { DisclaimerText, Subtext } from '../GoogleTagManager/style';

const defaultFormState: Partial<MatomoAnalyticsForm> = {
  matomoUrl: '',
  matomoSiteIds: [],
};

const Matomo = () => {
  const { withPageProgressHandler } = useBusyProgressActions();
  const getT = getTWithPathKey('pages.settings.analyticsProviders.matomo');
  const { handleServerError } = useServerErrorActions();
  const { isSubmitting, withHandlingSubmittingState } = useSubmittingState();

  const [initialFormState, setInitialFormState] = useState<Partial<MatomoAnalyticsForm>>(defaultFormState);
  const [showDialog, setShowDialog] = useState(false);
  const [customDomains, setCustomDomains] = useState<CustomDomain[]>([]);
  const [formState, setFormState] = useState<Partial<MatomoAnalyticsForm>>(defaultFormState);

  useEffect(() => {
    getData();
  }, []);

  const getData = withPageProgressHandler(async () => {
    const customDomains = await getCustomDomains() as CustomDomain[];

    return analyticsProviderModel.getMatomoSettings()
      .then(analyticsProvider => {
        const data: MatomoAnalyticsForm = {
          ...analyticsProvider,
          matomoSiteIds: merge(
            customDomains.map(domain => ({
              [domain.id]: ''
            })), analyticsProvider.matomoSiteIds)
        };

        saveMatomoSettings(data);
        setCustomDomains(customDomains);
      })
  });

  const getCustomDomains = (() =>
    customDomainsModel
      .getCustomDomains()
      .then(({ externalHosts }) => externalHosts)
      .catch(handleServerError)
  );

  const saveMatomoSettings = (data: Partial<MatomoAnalyticsForm>) => {
    setInitialFormState(data);
    setFormState(data);
  };

  const handleSubmit = withHandlingSubmittingState(() =>
    analyticsProviderModel
      .updateMatomoSettings({ analyticsProvider: formState })
      .then(analyticsProvider => saveMatomoSettings(analyticsProvider))
      .catch(handleServerError)
  );

  const handleChangeInput = (value: MatomoAnalyticsForm) => {
    setFormState(prevState => ({
      ...prevState,
      ...value
    }));
  };

  const handleSiteIdChange = (index: number) => (value: any) => {
    setFormState(prevState => ({
      ...prevState,
      matomoSiteIds: prevState.matomoSiteIds?.map((item, i) => (
          index === i ?
            { ...item, ...value } :
            { ...item }
        ))
    }));
  };

  const handleDelete = () => {
    setShowDialog(false);

    analyticsProviderModel
      .updateMatomoSettings({ analyticsProvider: defaultFormState })
      .then(getData)
      .catch(handleServerError)
  };

  const showSaveButton = useMemo(() =>
    !isEqual(initialFormState, formState), [formState, initialFormState]);

  return (
    <>
      <PageHeader title={getT('pageTitle')} backLink={ROUTES.analyticsProviders} />
      <SettingsForm isSaveButtonVisible={showSaveButton} handleSave={handleSubmit} id="matomo">
        <CardComponent title={getT('cardTitle')} isLoading={isSubmitting}>
          <Grid container spacing={6} direction="column">
            <Grid item>
              <Subtext>{getT('cardSubText')}</Subtext>
            </Grid>
            <Grid item>
              <TextValidatorComponent
                style={{ maxWidth: '468px' }}
                label={getT('inputLabel')}
                disabled={initialFormState.matomoUrl}
                validators={[VALIDATORS_MAP.notEmpty('Matomo url'), VALIDATORS_MAP.https('Matomo url')]}
                value={formState.matomoUrl ?? ''}
                fieldName="matomoUrl"
                handleChange={handleChangeInput}
                disableMinHeight
              />
            </Grid>
            <Grid item>
              <DisclaimerText>{getT('disclaimer')}</DisclaimerText>
            </Grid>
            <Grid item>
              <Divider />
            </Grid>
            <Grid item>
              <Typography variant="h4" display="inline">{getT('siteIdCardTitle')}</Typography>
              <Subtext>{getT('siteIdSubText')}</Subtext>
            </Grid>
            {!customDomains.length ?
              <Grid item>
                <ColoredBox variant="violet with-border">
                  <Box my={5} mx={2}>
                    <Typography variant="body2" className="inherit">{getT('siteIdPromptText')}</Typography>
                  </Box>
                </ColoredBox>
              </Grid> :
              customDomains.map((domain, index) =>
                <Grid item key={`custom-domains-${index}`}>
                  <Box display="flex" flexWrap="wrap" alignItems="center">
                    <Box mr={5} style={{ minWidth: '210px' }}>
                      <Typography variant="h4">{getT('siteIdColumnDomainTitle')}</Typography>
                      <Subtext>{domain.host}</Subtext>
                    </Box>
                    <Box>
                      <Typography variant="h4">{getT('siteIdColumnSiteIdTitle')}</Typography>
                      <TextValidatorComponent
                        InputLabelProps={{ shrink: false }}
                        disabled={initialFormState.matomoSiteIds?.[index]?.[domain.id]}
                        validators={[VALIDATORS_MAP.notEmpty('a site ID'), VALIDATORS_MAP.numbersOnly]}
                        value={formState.matomoSiteIds?.[index]?.[domain.id] ?? ''}
                        fieldName={domain.id.toString()}
                        disableMinHeight
                        noWrap
                        handleChange={handleSiteIdChange(index)}
                        style={{ width: '4vw', minWidth: '88px' }}
                      />
                    </Box>
                  </Box>
                </Grid>
              )
            }
            <Grid item>
              <Divider />
            </Grid>
            <Grid item>
              <Box display="flex">
                <StatusComponent
                  statusText="Delete Matomo Settings"
                  status={UPLOAD_STATUSES.SYNC_FAILED}
                  style={{
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowDialog(true)}
                />
              </Box>
            </Grid>
          </Grid>
          {showDialog &&
            <ConfirmationDialog
              open
              title={getT('confirmationDialogTitle')}
              showWarningIcon={false}
              message={getT('confirmationDialogMessage')}
              confirmText={getT('confirmationDialogConfirmText')}
              handleYes={() => handleDelete()}
              handleNo={() => setShowDialog(false)}
            />
          }
        </CardComponent>
      </SettingsForm>
    </>
  );
};

export default Matomo;
